import React, { memo, useCallback, useContext, useMemo } from 'react'
import { Button, Space, Tooltip } from 'antd'
import moment from 'moment'
import {
  NotLinkButton,
  TaskButtonCloseWrapper,
  TimeLineContentHeaderWrapper,
  TimeLineContentWrapper,
} from '../../../styles/styles'
import { modalOpen } from './activity-timeline-task-modal'
import { ActivityTask, StageCodeTask } from '../../../types/types'
import { Avatar } from '../../../../../../ui/Avatar'
import TimelineItemHeader from '../activity-timeline-item-header/activity-timeline-item-header'
import TimelineItemHeaderText from '../activity-timeline-item-header-text/activity-timeline-item-header-text'
import { TimelineItemProps } from '../activity-timeline-Item'
import { completeTask } from '../../../api/completeTask'
import { formatDate } from '../../../hooks/useFormatDate'
import { ActivityTimelineContext } from '../../../context/activity-timeline-context'

type ActivityTimelineTaskType = Omit<TimelineItemProps, 'data'>
interface ActivityTimelineTaskProps extends ActivityTimelineTaskType {
  className?: string
  data?: ActivityTask
}

function getTaskBackgroundColor(stageCode?: StageCodeTask) {
  if (stageCode === 'done') {
    return '#40a9ff63'
  }
  if (stageCode === 'new') {
    return '#f4f9ee'
  }
  return '#ffe8e8'
}

const ActivityTimelineTask: React.FC<ActivityTimelineTaskProps> = memo(
  (props: ActivityTimelineTaskProps) => {
    const { date, data, user, scopeDate, index, type } = props
    const { updateTimeline } = useContext(ActivityTimelineContext)

    const currentDate = moment().valueOf()
    const isCurrentDateLess = parseInt(date) <= currentDate
    const dotColor = isCurrentDateLess ? '#2fc4f5' : '#97c807'
    const backgroundColor = getTaskBackgroundColor(data?.stageCode)

    const isExpired = 'expired' === data?.stageCode
    const isButtonClose = 'done' !== data?.stageCode

    // const isExpired = useMemo(() => 'expired' === data?.stageCode, [data])
    // const isButtonClose = useMemo(() => 'done' !== data?.stageCode, [data])

    const onTaskClose = useCallback(async () => {
      const result = await modalOpen()
      if (!result) {
        return
      }
      if (!Boolean(result?.complete_reason)) {
        return
      }

      await completeTask({
        id: data?.id,
        complete_reason: result.complete_reason,
      }).then(() => {
        if (updateTimeline) {
          updateTimeline?.({
            data: {
              ...data,
              stage: 'Завершена',
              stageCode: 'done',
              complete_reason: result.complete_reason,
            },
            type: type,
            date: scopeDate,
            index,
          })
        }
      })
    }, [data, updateTimeline])

    const responsibleUsers = useMemo(() => {
      return data?.responsible?.map(user => `${user?.name} (${user?.id})`).join(', ')
    }, [data?.responsible])

    const formatDateDeadline = useMemo<string>(() => formatDate(data?.deadline), [data?.deadline])

    const formatDateCompleted = useMemo<string>(
      () => formatDate(data?.completed_at),
      [data?.completed_at],
    )

    return (
      <>
        <TimeLineContentWrapper color={backgroundColor}>
          <TimeLineContentHeaderWrapper>
            <Space>
              <TimelineItemHeader date={date} title={<NotLinkButton>Задача</NotLinkButton>} />
            </Space>
            <Space>
              <Avatar src={user?.icon} alt={user?.name} />
              <Tooltip placement="top" title="Автор задачи">
                <span>{user?.name}</span>
              </Tooltip>
            </Space>
          </TimeLineContentHeaderWrapper>

          {isExpired && (
            <span
              style={{ color: '#ca0808', fontWeight: 'bold' }}
            >{`Крайний срок: ${formatDateDeadline}`}</span>
          )}

          {data?.responsible && (
            <TimelineItemHeaderText title="Ответственные:" text={responsibleUsers} />
          )}
          <TimelineItemHeaderText title="Стадия:" text={data?.stage} />
          <TimelineItemHeaderText title="Тип:" text={data?.task_type} />
          {data?.task && <TimelineItemHeaderText title="Комментарий:" text={data?.task} />}
          {data?.completed_at && <span>{`Дата завершения: ${formatDateCompleted}`}</span>}
          {data?.complete_reason && <span>{`Причина завершения: ${data?.complete_reason}`}</span>}
          {isButtonClose && (
            <TaskButtonCloseWrapper>
              <Button onClick={onTaskClose} size={'small'}>
                Завершить задачу
              </Button>
            </TaskButtonCloseWrapper>
          )}
        </TimeLineContentWrapper>
      </>
    )
  },
)

ActivityTimelineTask.displayName = 'ActivityTimelineTask'
export default ActivityTimelineTask
